
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { ConfigurationService, EventsService } from '@app/_shared/providers';
import { MIXPANEL_EVENTS } from '@app/_shared/providers/mixpanel/mixpanel.events';
import { MixpanelService } from '@app/_shared/providers/mixpanel/mixpanel.service';
import { SIDEBAR_BUBBLE_EVENTS } from '@app/_shared/shared.enums';
import { UserManagementService } from '@app/modules/admin/providers/user-management/user-management.service';
import { AlertsService } from '@app/modules/trading/modules/dashboard/providers/alerts/alerts.service';
import { LoginSource } from '@app/modules/trading/modules/profile/components/personal-details/interfaces/personal-details.enum';
import { NotificationsService } from '@app/modules/trading/modules/profile/providers/notifications/notifications.service';
import { MbPlansService } from '@app/modules/trading/modules/mb-plans/providers/mb-plans.service';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { WebAlertStatus } from '../../../../utProto/webAlertStructureEnums_pb';
import { CONFIGURABLE_IMAGES, CustomEvents, DASHBOARD_PATH, MARKET_WATCH_PATH, SELECTED_VENDOR, StorageHelper, Utils, UTRADE_ONE_AI_PATH, WHATS_NEW_PATH } from '../../../_helpers';
import { AuthRole, AuthService } from '../../../modules/auth';
import { ProfileMenuComponent } from '../profile-menu/profile-menu.component';
import { createStrategies, headerItems, toolItems } from '../side-bar/sidebar-items';
import { SideBarItemI } from '../side-bar/sidebar.interfaces';
import { MarketPlaceService } from '@app/modules/trading/modules/market-place/providers/market-place/market-place.service';
import { NotificationsComponent } from '@app/modules/trading/modules/profile/components/notifications/notifications.component';
import { ChatService } from '@app/modules/trading/modules/create-with-ai/providers/chat.service';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, OnDestroy {
    @Input() compact = false;
    isAdmin = false
    activeUsersCount = 0;
    SELECTED_VENDOR = SELECTED_VENDOR;
    AuthRole = AuthRole;
    actionRequiredAlertsCount = 0
    headerItems = headerItems;
    toolItems = toolItems;
    createPortfolioMethods = createStrategies;
    searchControl = new UntypedFormControl();
    userName: string;
    LoginSource = LoginSource;

    notificationCount: number = 0;
    isMenuOpen = false;

    private subscriptions = new Subscription();
    preventEvent = Utils.preventEvent;

    hideHeader: boolean = false;

    @Input() sideMenuOpen: boolean = false;
    @Output() toggleMenu = new EventEmitter<boolean>();

    activeRoute: string = '';
    private routerSubscription: Subscription;
    whatsNewLink: boolean;

    notificationRef: MatDialogRef<NotificationsComponent> | null = null;
    @ViewChild('notificationIcon', { read: ElementRef }) notificationIconRef: ElementRef<HTMLElement>;

    profileMenuRef: MatDialogRef<ProfileMenuComponent> | null = null;
    @ViewChild('profileIcon', { read: ElementRef }) profileIconRef: ElementRef<HTMLElement>;

    isFixedAiOpened: boolean = true;
    isUtradeIntelligenceRoute: boolean = false;
    isDashboardRoute: boolean = false;
    logoUrl: string = CONFIGURABLE_IMAGES.logoUrl;

    constructor(
        public authService: AuthService,
        public alertsService: AlertsService,
        private eventService: EventsService,
        public notificationsService: NotificationsService,
        private router: Router,
        public umsService: UserManagementService,
        private matDialog: MatDialog,
        private mbPlansService: MbPlansService,
        private activatedRoute: ActivatedRoute,
        private configurationService: ConfigurationService,
        private mixpanel: MixpanelService,
        private marketPlaceService: MarketPlaceService,
        private dialog: MatDialog,
        private chatService: ChatService,
    ) {
        this.isFixedAiOpened = this.chatService.fixedAiOpened;
        this.checkRoutes(this.router.url);
        this.userName = this.authService.accessTokenObj?.clientId.toUpperCase();
        this.isAdmin = this.authService.accessTokenObj?.role === AuthRole.ADMIN;
        if (this.configurationService.remoteConfigCacheObj.MARKET_WATCH_ENABLED) {
            this.headerItems.find(item => item.route === MARKET_WATCH_PATH).visible = true;
        }
        // if (this.userName && !this.isAdmin) {
        //     // Check for external strategy permission
        //     this.mbPlansService.getUserDetails().subscribe((res => {

        //         if (res.isOneAiAllowed()) {
        //             headerItems.find(item => item.route === UTRADE_ONE_AI_PATH).visible = true;
        //         }
        //     }))
        // }
        this.router.events.subscribe((event: any) => {
            if (event instanceof NavigationEnd) {
              this.checkRoutes(event.urlAfterRedirects);
            }
          })

        this.eventService.subscribe(CustomEvents.FIXED_AI_OPENED, (opened: boolean) => {
            this.isFixedAiOpened = opened;
        })
    }

    toggleSideMenu() {
        this.toggleMenu.emit(!this.sideMenuOpen);
    }

    ngOnInit() {
        if (this.authService.accessTokenObj) {
            this.alertsService.alertUpdate.subscribe(() => {
                this.getAlertCount();
            });
        }

        this.notificationCount = this.notificationsService.unreadMessages;

        this.notificationsService.notificationUpdate.subscribe((count) => {
            this.notificationCount = count;
        })

        this.activatedRoute.queryParams.subscribe(params => {
            if ('fromMobile' in params) {
                this.hideHeader = params['fromMobile'] === 'true';
            }
        });

        this.configurationService.getServerConfig().subscribe((res) => {
            this.whatsNewLink = !!res.config_params.WHATS_NEW_LINK;
        })

        // Initialize active route on component load
        this.activeRoute = this.router.url;

        // Subscribe to router events to track active route
        this.routerSubscription = this.router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).subscribe(() => {
            this.activeRoute = this.router.url;
        });
    }

    getAlertCount() {
        const dataSource = this.alertsService.alertsObj
            .filter(a => a.condition_status === WebAlertStatus.WEBALERTSTATUS_PENDING)
        this.actionRequiredAlertsCount = dataSource.length;
    }

    openAlerts() {
        this.eventService.publish(CustomEvents.HideNotificationBadge, SIDEBAR_BUBBLE_EVENTS.ALERT);
    }

    openProfileCard() {
        if (this.profileMenuRef) {
            this.profileMenuRef.close();
        }

        const iconPosition = this.profileIconRef?.nativeElement?.getBoundingClientRect() ?? null;
        this.profileMenuRef = this.matDialog.open(ProfileMenuComponent, {
            width: '290px',
            position: {
                top: `${(iconPosition?.top || 14) + 46}px`,
                right: '24px'
            },
            panelClass: 'profile-menu',
            backdropClass: 'bg-transparent',
            disableClose: false,
        });
    }

    get notificationBadge() {
        return this.notificationCount > 10 ? '10+' : this.notificationCount;
    }

    openNotificationPanel() {
        if (this.notificationRef) {
            this.notificationRef.close();
        }

        const iconPosition = this.notificationIconRef?.nativeElement?.getBoundingClientRect() ?? null;
        this.notificationRef = this.dialog.open(NotificationsComponent, {
            width: '30vw',
            position: {
                top: `${(iconPosition?.top || 14) + 38}px`,
                right: '40px'
            },
            panelClass: 'notification-panel',
            backdropClass: 'bg-transparent',
            disableClose: false,
        })
        
        this.notificationRef.afterClosed().subscribe(() => {
            if (this.notificationsService.unreadMessages > 0) {
                this.notificationsService.markAllAsRead(this.notificationsService.selectedTab);
            }
            this.notificationRef = null;
        });
        this.mixpanel.track(MIXPANEL_EVENTS.OPEN_NOTIFICATIONS);
    }

    //Redirects to configurable link
    goToUrl() {
        window.location.href = SELECTED_VENDOR.websiteLink;
    }

    //Redirects to dashboard
    home() {
        this.router.navigate(['/']);
    }


    redirectToHome() {
        if (this.sideMenuOpen) {
            this.toggleMenu.emit(false);
        }
        this.router.navigate([DASHBOARD_PATH]);
    }

    createNewPortfolio(route) {
        this.router.navigate([route])
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
        if (this.routerSubscription) {
            this.routerSubscription.unsubscribe();
        }
    }

    isActiveRoute(tab: any): boolean {
        if (!tab.tool) {
            // For regular tabs, direct route comparison
            return this.activeRoute.startsWith(tab.route);
        }
        // For tool menu, check if any tool route is active
        return this.toolItems.some(tool =>
            this.activeRoute.startsWith(tool.route) && tool.visible !== false
        );
    }

    openWhatsNew() {
        this.router.navigate([WHATS_NEW_PATH]);
        this.mixpanel.track(MIXPANEL_EVENTS.WHATS_NEW);
    }

    navigateToComponent(item: SideBarItemI) {
        this.router.navigate([item.route]);
        if (item.event) {
            switch (item.event) {
              case MIXPANEL_EVENTS.NAVIGATE_PORTFOLIOS:
                const tabIndex = StorageHelper.currentStrategyTab
                this.mixpanel.track(item.event, { section: this.marketPlaceService?.portfolioTabsIndices?.[tabIndex] });
                break;
              case MIXPANEL_EVENTS.NAVIGATE_PAY_OFF_CURVE:
                this.mixpanel.track(item.event, { from: "Dashboard" });
                break;
              case MIXPANEL_EVENTS.NAVIGATE_MARGIN_CALCULATOR:
                this.mixpanel.track(item.event, { from: "Dashboard" });
                break;
              default:
                this.mixpanel.track(item.event);
                break;
            }
          }
    }

    checkRoutes(route: string) {
        const pathWithoutQuery = route.split('?')[0];
        this.isUtradeIntelligenceRoute = pathWithoutQuery === UTRADE_ONE_AI_PATH;
        this.isDashboardRoute = pathWithoutQuery === DASHBOARD_PATH;
    }

    fixedAifunction() {
        if (this.isUtradeIntelligenceRoute) return;
        if (this.isDashboardRoute) {
            this.router.navigate([UTRADE_ONE_AI_PATH], { queryParams: { threadId: this.chatService.currentFixedAiThreadId } });
            return;
        }

        this.isFixedAiOpened = !this.isFixedAiOpened;
        this.chatService.fixedAiOpened = this.isFixedAiOpened;
        this.eventService.publish(CustomEvents.FIXED_AI_OPENED, this.isFixedAiOpened);
    }
}
